import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/shared/lib/utils'

export interface ToggleSwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /**
   * Состояние переключателя
   * @default false
   */
  checked?: boolean
  /**
   * Callback при изменении состояния
   */
  onChange?: (checked: boolean) => void
  /**
   * Callback для haptic feedback при переключении
   */
  onHapticFeedback?: (type?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
  /**
   * Отключенное состояние
   */
  disabled?: boolean
  /**
   * Подпись для доступности
   */
  'aria-label'?: string
}

/**
 * UI компонент: Премиальный Свитчер 2025
 *
 * Интерактивный переключатель двух состояний (Включено / Выключено),
 * выполненный в премиальной эстетике 2025 года.
 *
 * @example
 * ```tsx
 * <ToggleSwitch
 *   checked={enabled}
 *   onChange={(checked) => setEnabled(checked)}
 *   aria-label="Включить уведомления"
 * />
 * ```
 */
export const ToggleSwitch = forwardRef<HTMLButtonElement, ToggleSwitchProps>(
  (
    {
      checked = false,
      onChange,
      onHapticFeedback,
      disabled = false,
      className,
      'aria-label': ariaLabel,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return

      onHapticFeedback?.('light')
      onChange?.(!checked)
      onClick?.(e)
    }

    return (
      <motion.button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          // Базовые размеры: трек овал с высотой 26-30px, ширина 52px
          'relative inline-flex h-[28px] w-[52px] rounded-full',
          // Минимальный touch target
          'min-w-[44px]',
          // Переходы цвета фона
          'transition-colors duration-150',
          // Фокус для доступности
          'focus:outline-none',
          // Отключенное состояние
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
          // Оптимизация для touch
          'touch-manipulation select-none',
          // Убираем любые border
          'border-0',
          // Цвета трека
          {
            // Light theme: Off - #EAE6E1, On - оранжево-янтарный с градиентом
            'bg-[#EAE6E1]': !checked && !disabled,
            // Dark theme: Off - #1A1C1E
            'dark:bg-[#1A1C1E]': !checked && !disabled,
            // Active state: градиент для премиального эффекта
            'bg-gradient-to-r from-[#E85A1C] to-[#D24D12]': checked && !disabled,
            'dark:bg-gradient-to-r dark:from-[#D24D12] dark:to-[#BC3F0C]': checked && !disabled,
          },
          className
        )}
        whileTap={!disabled ? { scale: 0.97 } : undefined}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        {...(props as any)}
      >
        {/* Бегунок - белый/почти белый с внутренней тенью */}
        <motion.span
          className={cn(
            // Абсолютное позиционирование
            'absolute top-1/2 left-[4px]',
            // Диаметр ручки минимум 22-24px (делаем 22px)
            'h-[22px] w-[22px] rounded-full',
            // Бегунок всегда белый (#FFFFFF)
            'bg-[#FFFFFF]',
            // Внутренняя тень для глубины
            'shadow-[inset_0_1px_2px_rgba(0,0,0,0.1),0_1px_4px_rgba(0,0,0,0.12)]',
            // Тень для тёмной темы более выраженная
            'dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2),0_1px_4px_rgba(0,0,0,0.45)]',
            // Убеждаемся что бегунок выше трека
            'z-10'
          )}
          initial={{ x: 0, y: '-50%' }}
          animate={{
            // Позиция бегунка: при checked сдвигаем вправо
            // 52px (ширина трека) - 22px (размер бегунка) - 4px (отступ справа) - 4px (начальный left) = 22px
            x: checked ? 22 : 0,
            y: '-50%',
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 17,
            duration: 0.18, // 180ms - в диапазоне 160-220ms
          }}
        />
      </motion.button>
    )
  }
)

ToggleSwitch.displayName = 'ToggleSwitch'

